'use client';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { graphql } from 'src/gql';

import { USER_ROLE_ADVISOR, USER_ROLE_ASSISTANT, USER_ROLE_CSA } from '@savvy/constants/user-roles';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

export const ORGANIZATION_BRANDING_ENABLED_QUERY = graphql(/* GraphQL */ `
  query OrganizationBrandingEnabled {
    advisor {
      id
      organization {
        id
        brandingEnabled
      }
    }
  }
`);

const ORGANIZATION_BRANDING_LD_FLAG = 'advisorGroupBranding';

/**
 * Whether the Organization Branding feature is available to the current user's org.
 *
 * The org setting is the source of truth once someone has made a choice. A null
 * setting means nobody has, so the legacy `advisorGroupBranding` LaunchDarkly flag
 * still decides — that keeps the ~114 advisors already targeted in LD switched on
 * without a backfill.
 */
export function useOrganizationBrandingEnabled() {
  const { user } = useAuth();
  const flags = useFlags();

  const role = user?.role;
  // `advisor` resolves to the current user, so this also skips before auth lands —
  // an unresolved user has no role and can't match.
  const isAdvisorOrStaff = role === USER_ROLE_ADVISOR || role === USER_ROLE_ASSISTANT || role === USER_ROLE_CSA;

  const { data, loading } = useQueryWithErrorLogging(ORGANIZATION_BRANDING_ENABLED_QUERY, {
    skip: !isAdvisorOrStaff,
  });

  const organizationSetting = data?.advisor.organization?.brandingEnabled;

  return {
    enabled: organizationSetting ?? flags[ORGANIZATION_BRANDING_LD_FLAG] === true,
    loading,
  };
}
